import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, RotateCcw, Save, Trash2, FlaskConical, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { setPageMeta, fmtDate } from '../lib/shop';

const NOTICE = 'This calculator is provided for laboratory calculation purposes only. It does not provide medical, dosing or administration advice and is not intended for human or veterinary use.';

const Notice = ({ testid }) => (
  <div className="bg-[#FFF7ED] border border-[#FDE68A] p-4 flex gap-3" data-testid={testid}>
    <ShieldAlert className="w-5 h-5 text-[#D97706] shrink-0" />
    <p className="text-[12px] text-[#92400E] leading-relaxed">{NOTICE}</p>
  </div>
);

const MG_PRESETS = [5, 10, 20, 30, 50];
const ML_PRESETS = [1, 2, 3, 5, 10];

export default function Calculator() {
  const { user } = useAuth();
  const [mg, setMg] = useState('10');
  const [ml, setMl] = useState('2');
  const [target, setTarget] = useState('');
  const [targetUnit, setTargetUnit] = useState('mg/ml');
  const [aliquotCount, setAliquotCount] = useState('');
  const [aliquotVol, setAliquotVol] = useState('');
  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPageMeta('Research Calculator', 'Neutral laboratory calculation tool: quantity, volume, concentration and equal-aliquot maths.');
  }, []);

  useEffect(() => {
    if (user) api.get('/calculations/mine').then((r) => setSaved(r.data)).catch(() => {});
  }, [user]);

  const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) && x >= 0 ? x : null; };
  const mgN = n(mg), mlN = n(ml);

  const results = useMemo(() => {
    if (!mgN || !mlN || mlN === 0) return null;
    const mgml = mgN / mlN;
    const r = {
      quantity_mg: mgN, volume_ml: mlN,
      mg_per_ml: +mgml.toFixed(4), mcg_per_ml: +(mgml * 1000).toFixed(1),
      mcg_total: +(mgN * 1000).toFixed(0),
    };
    const tN = n(target);
    if (tN && tN > 0) {
      const targetMgMl = targetUnit === 'mg/ml' ? tN : tN / 1000;
      r.target_mg_ml = +targetMgMl.toFixed(4);
      r.solvent_needed_ml = +(mgN / targetMgMl).toFixed(3);
    }
    const cN = n(aliquotCount);
    if (cN && cN > 0) {
      r.aliquot_count = cN;
      r.aliquot_volume_ml = +(mlN / cN).toFixed(4);
      r.aliquot_amount_mg = +(mgN / cN).toFixed(4);
      r.aliquot_amount_mcg = +((mgN / cN) * 1000).toFixed(1);
    }
    const aV = n(aliquotVol);
    if (aV && aV > 0 && aV <= mlN) {
      r.fixed_aliquot_volume_ml = aV;
      r.fixed_aliquot_count = Math.floor(mlN / aV);
      r.fixed_aliquot_amount_mg = +(mgml * aV).toFixed(4);
      r.fixed_aliquot_amount_mcg = +(mgml * aV * 1000).toFixed(1);
    }
    return r;
  }, [mgN, mlN, target, targetUnit, aliquotCount, aliquotVol]);

  const copy = () => {
    if (!results) return;
    const lines = [
      'VQ HEALTH — RESEARCH CALCULATION', '—'.repeat(30),
      `Compound quantity: ${results.quantity_mg} mg (${results.mcg_total} mcg)`,
      `Solution volume: ${results.volume_ml} mL`,
      `Concentration: ${results.mg_per_ml} mg/mL = ${results.mcg_per_ml} mcg/mL`,
    ];
    if (results.solvent_needed_ml) lines.push(`Target ${results.target_mg_ml} mg/mL → solvent volume needed: ${results.solvent_needed_ml} mL`);
    if (results.aliquot_count) lines.push(`${results.aliquot_count} equal laboratory aliquots: ${results.aliquot_volume_ml} mL each, ${results.aliquot_amount_mg} mg (${results.aliquot_amount_mcg} mcg) each`);
    if (results.fixed_aliquot_count) lines.push(`Aliquots of ${results.fixed_aliquot_volume_ml} mL: ${results.fixed_aliquot_count} aliquots, ${results.fixed_aliquot_amount_mg} mg (${results.fixed_aliquot_amount_mcg} mcg) each`);
    lines.push('—'.repeat(30), NOTICE);
    navigator.clipboard.writeText(lines.join('\n')).then(() => toast.success('Result copied'));
  };

  const reset = () => { setMg('10'); setMl('2'); setTarget(''); setAliquotCount(''); setAliquotVol(''); };

  const save = async () => {
    if (!results) return;
    if (!saveName.trim()) { toast.error('Give the calculation a name'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/calculations', { name: saveName.trim(), payload: { inputs: { mg, ml, target, targetUnit, aliquotCount, aliquotVol }, results } });
      setSaved((s) => [data, ...s]);
      setSaveName('');
      toast.success('Calculation saved to your account');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/calculations/${id}`).catch(() => {});
    setSaved((s) => s.filter((x) => x.id !== id));
  };

  const inp = "w-full border-2 border-[#0B0F12] bg-white px-4 py-4 text-lg font-mono2 font-semibold focus:outline-none focus:border-[#E54D2E]";
  const presetBtn = (active) => `px-4 py-2.5 font-mono2 text-xs border transition-colors ${active ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] bg-white hover:border-[#0B0F12]'}`;

  const Step = ({ num, title, children, testid }) => (
    <section className="border hairline bg-white p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 bg-[#0B0F12] text-white flex items-center justify-center font-mono2 font-bold text-sm">{num}</span>
        <p className="font-display font-black uppercase text-xl tracking-tight">{title}</p>
      </div>
      {children}
    </section>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="calculator-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95] flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Laboratory Tool</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">VQ Research Calculator</h1>
      <p className="text-[#575B60] mt-3 max-w-2xl">Neutral laboratory mathematics: quantity, volume, concentration and equal-aliquot calculations for in-vitro research work.</p>

      <div className="mt-6"><Notice testid="calculator-notice-top" /></div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 mt-8 items-start">
        <div className="space-y-5">
          <Step num="1" title="Compound Quantity" testid="calc-step-1">
            <div className="flex flex-wrap gap-2 mb-3">
              {MG_PRESETS.map((v) => (
                <button key={v} data-testid={`preset-mg-${v}`} onClick={() => setMg(String(v))} className={presetBtn(mg === String(v))}>{v} mg</button>
              ))}
            </div>
            <div className="flex items-center gap-3 max-w-xs">
              <input data-testid="calc-mg-input" type="number" min="0" step="any" value={mg} onChange={(e) => setMg(e.target.value)} className={inp} placeholder="Custom" />
              <span className="font-mono2 text-sm font-bold uppercase">mg</span>
            </div>
          </Step>

          <Step num="2" title="Solution Volume" testid="calc-step-2">
            <div className="flex flex-wrap gap-2 mb-3">
              {ML_PRESETS.map((v) => (
                <button key={v} data-testid={`preset-ml-${v}`} onClick={() => setMl(String(v))} className={presetBtn(ml === String(v))}>{v} mL</button>
              ))}
            </div>
            <div className="flex items-center gap-3 max-w-xs">
              <input data-testid="calc-ml-input" type="number" min="0" step="any" value={ml} onChange={(e) => setMl(e.target.value)} className={inp} placeholder="Custom" />
              <span className="font-mono2 text-sm font-bold uppercase">mL</span>
            </div>
          </Step>

          <Step num="3" title="Target Laboratory Concentration" testid="calc-step-3">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mb-3">Optional — calculates the solvent volume needed</p>
            <div className="flex items-center gap-3 max-w-md">
              <input data-testid="calc-target-input" type="number" min="0" step="any" value={target} onChange={(e) => setTarget(e.target.value)} className={inp} placeholder="e.g. 5" />
              <select data-testid="calc-target-unit" value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)}
                className="border-2 border-[#0B0F12] bg-white px-3 py-4 font-mono2 text-sm font-semibold focus:outline-none">
                <option value="mg/ml">mg/mL</option>
                <option value="mcg/ml">mcg/mL</option>
              </select>
            </div>
          </Step>

          <Step num="4" title="Laboratory Aliquot Calculation" testid="calc-step-4">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mb-3">Optional — equal in-vitro aliquot maths. Use either field.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95] block mb-1.5">Number of equal aliquots</label>
                <input data-testid="calc-aliquot-count" type="number" min="0" step="1" value={aliquotCount} onChange={(e) => setAliquotCount(e.target.value)} className={inp} placeholder="e.g. 10" />
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95] block mb-1.5">Or aliquot volume (mL)</label>
                <input data-testid="calc-aliquot-volume" type="number" min="0" step="any" value={aliquotVol} onChange={(e) => setAliquotVol(e.target.value)} className={inp} placeholder="e.g. 0.2" />
              </div>
            </div>
          </Step>
        </div>

        {/* RESULTS */}
        <div className="bg-[#0B0F12] text-[#FAFAF8] p-6 lg:sticky lg:top-32" data-testid="calc-results">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A8F95]">Result</p>
          {!results ? (
            <p className="text-sm text-[#9AA0A6] mt-4">Enter a compound quantity and solution volume to calculate.</p>
          ) : (
            <div className="mt-4 space-y-3 font-mono2">
              <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Total compound</p>
                <p className="text-2xl font-bold" data-testid="result-quantity">{results.quantity_mg} mg <span className="text-sm text-[#8A8F95]">({results.mcg_total} mcg)</span></p></div>
              <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Solution volume</p>
                <p className="text-2xl font-bold" data-testid="result-volume">{results.volume_ml} mL</p></div>
              <div className="border-t border-[#1E242B] pt-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Concentration</p>
                <p className="text-3xl font-bold text-[#E5B25D]" data-testid="result-concentration">{results.mg_per_ml} mg/mL</p>
                <p className="text-sm text-[#9AA0A6]" data-testid="result-concentration-mcg">= {results.mcg_per_ml} mcg/mL</p>
              </div>
              {results.solvent_needed_ml && (
                <div className="border-t border-[#1E242B] pt-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">For target {results.target_mg_ml} mg/mL</p>
                  <p className="text-xl font-bold" data-testid="result-solvent">{results.solvent_needed_ml} mL solvent needed</p>
                </div>
              )}
              {results.aliquot_count && (
                <div className="border-t border-[#1E242B] pt-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">{results.aliquot_count} equal aliquots</p>
                  <p className="text-xl font-bold" data-testid="result-aliquot">{results.aliquot_volume_ml} mL each</p>
                  <p className="text-sm text-[#9AA0A6]">{results.aliquot_amount_mg} mg ({results.aliquot_amount_mcg} mcg) per aliquot</p>
                </div>
              )}
              {results.fixed_aliquot_count > 0 && (
                <div className="border-t border-[#1E242B] pt-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Aliquots of {results.fixed_aliquot_volume_ml} mL</p>
                  <p className="text-xl font-bold" data-testid="result-aliquot-fixed">{results.fixed_aliquot_count} aliquots</p>
                  <p className="text-sm text-[#9AA0A6]">{results.fixed_aliquot_amount_mg} mg ({results.fixed_aliquot_amount_mcg} mcg) per aliquot</p>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <button data-testid="calc-copy" onClick={copy} disabled={!results}
              className="flex-1 bg-[#FAFAF8] text-[#0B0F12] py-3 font-mono2 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 hover:bg-white disabled:opacity-40 transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copy result
            </button>
            <button data-testid="calc-reset" onClick={reset}
              className="border border-[#2A3138] px-4 py-3 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:border-[#FAFAF8] transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          {user && results && (
            <div className="flex gap-2 mt-3">
              <input data-testid="calc-save-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Name this calculation"
                className="flex-1 bg-transparent border border-[#2A3138] px-3 py-2.5 text-sm focus:outline-none focus:border-[#FAFAF8]" />
              <button data-testid="calc-save" onClick={save} disabled={saving}
                className="border border-[#2A3138] px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:border-[#FAFAF8] transition-colors disabled:opacity-40">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          )}
          {!user && <p className="text-[11px] text-[#6B7178] mt-4"><Link to="/login" className="underline">Sign in</Link> to save named calculations to your account.</p>}
          <div className="mt-5 pt-4 border-t border-[#1E242B]">
            <p className="text-[11px] text-[#8A8F95] leading-relaxed" data-testid="calculator-notice-results">{NOTICE}</p>
          </div>
        </div>
      </div>

      {user && saved.length > 0 && (
        <section className="mt-10" data-testid="saved-calculations">
          <h2 className="font-display font-black uppercase text-2xl tracking-tight mb-4">Saved Calculations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {saved.map((s) => (
              <div key={s.id} className="border hairline bg-white p-4">
                <div className="flex justify-between items-start gap-2">
                  <p className="font-display font-bold uppercase">{s.name}</p>
                  <button data-testid={`saved-calc-delete-${s.id}`} onClick={() => del(s.id)} className="text-[#8A8F95] hover:text-[#E54D2E]"><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="font-mono2 text-xs text-[#575B60] mt-1">{s.payload?.results?.quantity_mg}mg / {s.payload?.results?.volume_ml}mL → {s.payload?.results?.mg_per_ml} mg/mL</p>
                <div className="flex justify-between items-center mt-2">
                  <p className="font-mono2 text-[10px] text-[#8A8F95]">{fmtDate(s.created_at)}</p>
                  <button data-testid={`saved-calc-load-${s.id}`} onClick={() => { const i = s.payload.inputs; setMg(i.mg); setMl(i.ml); setTarget(i.target); setTargetUnit(i.targetUnit); setAliquotCount(i.aliquotCount); setAliquotVol(i.aliquotVol); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="font-mono2 text-[10px] uppercase tracking-widest underline underline-offset-4">Load</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
